import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Loader2 } from "lucide-react";

/**
 * Server-rendered PDF page viewer.
 * The backend renders one page at a time to PNG (safe, no client-side JS PDF engine).
 * Tenant-scoped: only pages of jobs owned by the current tenant are returned.
 */
export default function PdfViewer({ jobId, totalPages, initialPage = 1 }) {
  const [page, setPage] = useState(initialPage);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try {
        const dpi = Math.round(120 * zoom);
        const res = await api.get(`/pdf-jobs/${jobId}/page/${page}`, {
          params: { dpi }, responseType: "blob",
        });
        if (!alive) return;
        const url = URL.createObjectURL(res.data);
        setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch (e) {
        if (alive) setError("Impossibile caricare la pagina");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [jobId, page, zoom]);

  const canPrev = page > 1;
  const canNext = page < (totalPages || 1);

  return (
    <div className="border border-border bg-white" data-testid="pdf-viewer">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 text-xs">
        <button disabled={!canPrev} onClick={() => setPage(p => p - 1)} className="p-1 border border-border disabled:opacity-40" data-testid="pdf-prev-page"><ChevronLeft size={14} /></button>
        <div className="font-mono">Pagina {page} / {totalPages || "?"}</div>
        <button disabled={!canNext} onClick={() => setPage(p => p + 1)} className="p-1 border border-border disabled:opacity-40" data-testid="pdf-next-page"><ChevronRight size={14} /></button>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="p-1 border border-border" data-testid="pdf-zoom-out"><ZoomOut size={14} /></button>
          <span className="font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="p-1 border border-border" data-testid="pdf-zoom-in"><ZoomIn size={14} /></button>
          <button onClick={() => setRotation(r => (r + 90) % 360)} className="p-1 border border-border" data-testid="pdf-rotate"><RotateCw size={14} /></button>
        </div>
      </div>
      <div className="p-3 overflow-auto max-h-[520px] flex items-start justify-center bg-muted/40">
        {loading && <Loader2 size={20} className="animate-spin text-muted-foreground mt-6" />}
        {!loading && error && <div className="text-destructive text-xs mt-6" data-testid="pdf-error">{error}</div>}
        {!loading && !error && imgUrl && (
          <img
            src={imgUrl}
            alt={`Pagina ${page}`}
            style={{ transform: `rotate(${rotation}deg)`, maxWidth: "100%", height: "auto" }}
            className="shadow-sm border border-border"
            data-testid={`pdf-page-${page}`}
          />
        )}
      </div>
    </div>
  );
}
